import Feature from 'ol/Feature.js';
import { useMapScale } from 'client/pages/MapPage/hooks/useMapScale';
import useDpi from '../../hooks/useDpi';
import { LAYER_CONFIG, MAX_SCALE } from '../../layers/constants';
import Point from 'ol/geom/Point.js';
import { bufferedPoint } from 'client/utils/helpers/Aoi';
import { useContext, useEffect, useState } from 'react';
import { MapContext, VectorLayer } from 'os-elements';
import { useDispatch, useSelector } from 'react-redux';
import { clearDrawStatus } from 'client/store/drawSlice/drawSlice';
import { addMapMode, removeMapMode } from 'client/store/mapSlice/mapSlice';
import { mapModes } from 'client/utils/constants/appStates';
import mobileAOIStyle from '../../layers/styles/mobileAOIStyle';
import VectorSource from 'ol/source/Vector';
import useDeviceType from 'client/utils/helpers/DeviceType/useDeviceType';
import { DESKTOP } from 'client/utils/constants/GDS/js/devices';
import PropTypes from 'prop-types';
const { DEFAULT, DRAWING, MODIFYING } = mapModes;

const OneTapAOIIndicator = ({ updateAoi, drawMobileArea, setDrawMobileArea, className }) => {
    const [centreCrossHair, setCentreCrossHair] = useState(new Feature());
    const [centerSquareFeature, setCenterSquareFeature] = useState(new Feature());
    const scale = useMapScale();
    const { getResolutionFromScale } = useDpi();
    const maxResForMobileAOI = getResolutionFromScale(MAX_SCALE);
    const { map } = useContext(MapContext);
    const aoiSource = useSelector(state => state.aoi.source);
    const dispatch = useDispatch();
    const deviceType = useDeviceType();

    const doesAoiExist = () => {
        const aoiExtent = aoiSource?.getExtent();
        return aoiExtent.some(value => value !== null && isFinite(value));
    };

    useEffect(() => {
        map.on('moveend', updateMobileAOIPreview);
        return () => {
            map.un('moveend', updateMobileAOIPreview);
        };
    }, []);

    const aoiExistsInitially = doesAoiExist();
    useEffect(() => {
        if (!aoiExistsInitially) {
            updateMobileAOIPreview();
        }
    }, [aoiExistsInitially]);

    const updateMobileAOIPreview = () => {
        const hasAOI = doesAoiExist();
        if (hasAOI || scale > MAX_SCALE) {
            return;
        }
        const center = map?.getView().getCenter();
        const newCrosshair = new Feature();
        newCrosshair.setGeometry(new Point([center[0], center[1]]));
        setCentreCrossHair(newCrosshair);
        const bufferedSquare = bufferedPoint(center, 50);
        setCenterSquareFeature(bufferedSquare);
    };

    const getMapFeaturesForMobileAOI = () => {
        if (!drawMobileArea) {
            return;
        }
        aoiSource.clear();
        aoiSource.addFeature(centerSquareFeature);
        updateAoi(aoiSource);
        dispatch(removeMapMode(DRAWING));
        dispatch(removeMapMode(MODIFYING));
        dispatch(addMapMode(DEFAULT));
        dispatch(clearDrawStatus());
        setDrawMobileArea(false);
    };
    useEffect(() => { getMapFeaturesForMobileAOI(); }, [drawMobileArea]);

    return <VectorLayer
        id={LAYER_CONFIG.oneTapPreviewAOI.id}
        visible={!doesAoiExist() && deviceType !== DESKTOP}
        source={new VectorSource({ features: [centreCrossHair, centerSquareFeature] }) }
        className={className}
        style={feature => mobileAOIStyle(feature)}
        zIndex={LAYER_CONFIG.oneTapPreviewAOI.zIndex}
        maxResolution={maxResForMobileAOI}
    />;
};

OneTapAOIIndicator.propTypes = {
    updateAoi: PropTypes.func,
    drawMobileArea: PropTypes.bool,
    setDrawMobileArea: PropTypes.func,
    className: PropTypes.string
};

OneTapAOIIndicator.defaultProps = {
    updateAoi: () => {},
    drawMobileArea: false,
    setDrawMobileArea: () => {},
    className: ''
};

export default OneTapAOIIndicator;
